import { getAuthorized } from "@/lib/fetch";

import { TTranslation } from "@/features/FormCard/hooks";
import { useQuery } from "@tanstack/react-query";

type TUseTranslation = {
  client: string;
  table_name: string;
  language: string;
};

/**
 * Custom hook to fetch translations for a given table
 *
 * This hook fetches translations for a specified table and language
 * and returns the translations and loading status
 *
 * @param {TUseTranslation} params - The parameters for the useTranslation hook
 * @param {string} params.client - The client name
 * @param {string} params.table_name - The table name
 * @param {string} params.language - The language code
 * @returns {Object} An object containing the translations and loading status
 */
export function useTranslation({
  client,
  table_name,
  language,
}: TUseTranslation) {
  if (!client || !table_name || !language)
    return { translations: undefined, isLoadingTranslations: false };

  const { data: translations, isLoading: isLoadingTranslations } =
    useQuery<TTranslation>({
      queryKey: [table_name, "translations", language],
      queryFn: async () => {
        const res = await getAuthorized(
          `${
            import.meta.env.VITE_BACKEND_URL
          }/${client}/${table_name}/translations?language=${language}`,
        );
        return res.json();
      },
    });

  return {
    translations,
    isLoadingTranslations,
  };
}
